import { useRouter } from 'next/router';
import { useQuery } from 'react-query';

import { boardsServices } from '@services';
import { QUERY_KEYS } from 'constants/queryKeys';
import { getCleanedUrlParams, getUrl } from '@utils';
import { URLS } from 'layouts/DrawerLayout/constants';
import { useAssignees } from 'hooks/common';
import {
  getBillingRequestPayload,
  getColumns,
  normaliseBillingTasks,
} from './utils';
import { BILLING_PAGE_SIZE, BILLING_TABS } from './constants';

export const useBillingOperations = () => {
  const {
    assignees,
    isLoading: isAssigneesLoading,
    isFetched: isAssigneesFetched,
  } = useAssignees();
  const router = useRouter();
  const page = router.query?.page || 1;
  const assignee = router.query?.assignee;
  const orderBy = router.query?.orderBy || 'createdAt';
  const order = router.query?.order || 'desc';
  const query = router.query?.query || '';
  const board = router.query?.board || Object.keys(BILLING_TABS)[0];
  const archived = router.query?.archived;
  const pageSize = router.query?.pageSize || BILLING_PAGE_SIZE;

  const params = {
    page,
    assignee,
    orderBy,
    order,
    board,
    query,
    archived,
    pageSize,
  };

  const requestUrlParams = {
    page,
    orderBy,
    order,
    query,
    pageSize,
  };

  const payload = getBillingRequestPayload(
    getCleanedUrlParams({
      ...params,
      boardId: BILLING_TABS[board].id,
    })
  );

  const getBillingExportPayload = () => {
    return {
      boardId: BILLING_TABS[board].id,
      payload: {
        type: 'csv',
        rule: payload,
      },
      params: getCleanedUrlParams(requestUrlParams),
    };
  };
  const billingResponse = useQuery(
    [
      QUERY_KEYS.GET_BILLING_KEY,
      {
        ...params,
        boardId: BILLING_TABS[board].id,
      },
    ],

    () =>
      boardsServices.getBoardTasks({
        boardId: BILLING_TABS[board].id,
        params: getCleanedUrlParams(requestUrlParams),
        payload,
      }),
    { enabled: isAssigneesFetched, refetchOnMount: true }
  );

  const enumsResponse = useQuery(
    [QUERY_KEYS.GET_BOARDS_ENUMS, BILLING_TABS[board].id],
    () => boardsServices.getEnumsPerBoardId(BILLING_TABS[board].id),
    { enabled: router.isReady }
  );

  const handlePageChange = (_event, newPage) => {
    router.push(getUrl({ ...router.query, page: newPage }, URLS.billing));
  };

  const handleSortBy = (sortByModel) => {
    const url = getUrl({ ...router.query, ...sortByModel }, URLS.billing);

    router.push(url);
  };

  const billingTasks = normaliseBillingTasks(
    billingResponse?.data?.data || [],
    assignees
  );

  const statuses = BILLING_TABS[board].statusOrder.reduce((acc, cur) => {
    acc.push({
      text: BILLING_TABS[board].statuses[cur].label,
      value: Number(cur),
    });
    return acc;
  }, []);

  return {
    page,
    pageSize: parseInt(pageSize, 10) || BILLING_PAGE_SIZE,
    assignees,
    total: billingResponse?.data?.meta?.total,
    loading: billingResponse?.isFetching || isAssigneesLoading,
    error: billingResponse?.error,
    billingTasks,
    board,
    statuses,
    enums: enumsResponse?.data?.data || {},
    columns: getColumns(billingTasks || [], board),
    handlePageChange,
    handleSortBy,
    refresh: billingResponse.refetch,
    getBillingExportPayload,
  };
};
